<script lang="ts">
	import { mutation } from "@api/graphql.js";
	import sendVoucherOrder from "@api/mutations/sendVoucherOrder.graphql";
	import Button from "@components/common/Button.svelte";
	import Link from "@components/common/Link.svelte";
	import Modal from "@components/common/Modal.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import { LinkVariant } from "@components/common/getLinkClass.js";
	import Form from "@components/form/Form.svelte";
	import LabelledInput from "@components/form/LabelledInput.svelte";
	import LabelledSelect from "@components/form/LabelledSelect.svelte";
	import type { HTMLFormAttributes } from "svelte/elements";
	import type { Procedure } from "@core/schema/Procedure.js";
	import type { VoucherOrderInput } from "@core/schema/VoucherOrder.js";
	import { getPrivacyPolicyPath } from "@core/schema/paths/getPrivacyPolicyPath.js";
	import type { Complete } from "@core/utils/Complete.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";

	interface Props extends Omit<HTMLFormAttributes, "onsubmit"> {
		procedures: Procedure[];
		selectedProcedureTitle?: string;
		successCallback?(): void;
	}

	// eslint-disable-next-line @typescript-eslint/unbound-method
	const { procedures, selectedProcedureTitle, successCallback, ...rest }: Props = $props();
	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();

	function formatPrice(price: number): string {
		return `${price.toLocaleString("cs-CZ")} Kč`;
	}

	let showSuccessModal = $state<boolean>(false);
	let errorHappened = $state<boolean>(false);
	let gdprApprovedAt = $state<string | null>(null);

	function onGdprToggle(event: Event): void {
		const input = event.target as HTMLInputElement;
		const newValue = input.checked ? new Date().toISOString() : null;
		gdprApprovedAt = newValue;
	}

	const send = createPromiseWithStatus(async (input: VoucherOrderInput) =>
		mutation<boolean, { input: typeof input }>(PUBLIC_GRAPHQL_ENDPOINT, sendVoucherOrder, { input }),
	);

	async function submit(event: SubmitEvent): Promise<void> {
		event.preventDefault();
		if (!gdprApprovedAt) {
			throw new Error("GDPR must be approved.");
		}

		errorHappened = false;

		const form = event.target as HTMLFormElement;
		const formData = new FormData(form);
		const email = formData.get("email") as string;
		const recipientFullName = formData.get("recipientFullName") as string;
		const recipientSalutation = formData.get("recipientSalutation") as string;
		const procedure = formData.get("procedure") as string;
		const deliveryMethod = formData.get("deliveryMethod") as "online" | "pickup";

		try {
			const input: Complete<VoucherOrderInput> = {
				email,
				gdprApprovedAt,
				voucherDetail: {
					recipientFullName,
					recipientSalutation,
					procedure,
					deliveryMethod,
				},
				locale: tenant.locale,
			};
			await send.call(input);
			showSuccessModal = true;
			form.reset();
			// Reset GDPR approval
			// eslint-disable-next-line require-atomic-updates
			gdprApprovedAt = null;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			errorHappened = true;
		}
	}
</script>

<Modal bind:show={showSuccessModal}>
	<div class="flex flex-col items-center gap-6 text-center">
		<div>
			<p class="text-xl md:text-2xl text-hazelnut font-heading font-bold mb-2">Děkujeme za objednávku</p>
			<p class="text-hazelnut/70">
				Vaše objednávka poukazu byla úspěšně odeslána.
				<br />
				Brzy se vám ozveme s dalšími informacemi.
			</p>
		</div>

		<Button
			variant={ButtonVariant.PrimaryMedium}
			onclick={(): void => {
				showSuccessModal = false;
				successCallback?.();
			}}
		>
			Rozumím
		</Button>
	</div>
</Modal>

<div class="flex flex-col items-center gap-6 w-full">
	<Form {...rest} onsubmit={submit} class={["flex flex-col gap-3 md:gap-5 w-full", rest.class]}>
		<LabelledInput type="email" name="email" required autocomplete="email" inputmode="email">E-mail</LabelledInput>

		<LabelledInput name="recipientFullName" required autocomplete="off">Celé jméno obdarovaného</LabelledInput>

		<LabelledInput name="recipientSalutation" required autocomplete="off">
			Komu je poukaz určen (např. pro Petru, pro milou maminku)
		</LabelledInput>

		<LabelledSelect name="procedure" required>
			Vyberte proceduru
			{#snippet options()}
				<option value="" disabled selected={!selectedProcedureTitle}>Vyberte proceduru</option>
				{#each procedures as procedure (procedure.id)}
					<option value={procedure.title} selected={selectedProcedureTitle === procedure.title}>
						{procedure.title} - {formatPrice(procedure.price)}
					</option>
				{/each}
			{/snippet}
		</LabelledSelect>

		<LabelledSelect name="deliveryMethod" required>
			Způsob doručení
			{#snippet options()}
				<option value="" disabled selected>Vyberte způsob doručení</option>
				<option value="online">Online (e-mailem)</option>
				<option value="pickup">Vyzvednout osobně</option>
			{/snippet}
		</LabelledSelect>

		<LabelledInput type="checkbox" wrapperClass="col-span-full" onchange={onGdprToggle} required>
			Souhlasím se
			<Link variant={LinkVariant.Underline} href={getPrivacyPolicyPath(tenant)}>
				Zásadami zpracování osobních údajů
			</Link>
		</LabelledInput>

		<Button variant={ButtonVariant.PrimaryMedium} disabled={send.value.status === "loading"} class="w-full mt-2">
			{#if send.value.status === "loading"}
				Odesílání…
			{:else}
				Odeslat objednávku
			{/if}
		</Button>
	</Form>

	{#if errorHappened}
		<p class="text-copper-300 text-center text-sm">
			Omlouváme se, ale při odesílání formuláře došlo k chybě. Zkuste to prosím znovu nebo později.
		</p>
	{/if}
</div>
